import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiSend, FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import LandingHeader from "../components/landing/LandingHeader";
import LandingFooter from "../components/landing/LandingFooter";
import SmartSchoolSeoContent from "../components/seo/SmartSchoolSeoContent";
import smartclassHero from "../static/images/smartclass-hero.png";
import smartclassChat from "../static/images/smartclass-chat.png";
import smartclassKnowledge from "../static/images/smartclass-knowledge.png";
import smartclassAnnouncements from "../static/images/smartclass-announcements.png";
import smartclassLogo from "../static/images/smartclass-logo.png";
import { createEarlyAccessProfile } from "../services/EarlyAccess";
import {
  AUTH_SESSION_CHANGED_EVENT,
  getPublicUserName,
  getStoredPublicUser,
} from "../services/Auth";

const initialForm = {
  schoolName: "",
  contactName: "",
  role: "",
  email: "",
  phone: "",
  students: "",
};

const getEarlyAccessFormWithUser = (user, currentForm = initialForm) => ({
  ...currentForm,
  contactName: currentForm.contactName || getPublicUserName(user),
  email: currentForm.email || user?.email || "",
  phone: currentForm.phone || user?.phoneNumber || "",
});

const SmartSchoolSystem = () => {
  const translation = useSelector((state) => state.translation);
  const [publicUser, setPublicUser] = useState(getStoredPublicUser);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  useEffect(() => {
    const updateUser = (event) => {
      const nextUser = event.detail || getStoredPublicUser();
      setPublicUser(nextUser);
      setFormData((current) => getEarlyAccessFormWithUser(nextUser, current));
    };

    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, updateUser);
    window.addEventListener("storage", updateUser);

    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, updateUser);
      window.removeEventListener("storage", updateUser);
    };
  }, []);

  const highlightItems = useMemo(
    () => [
      translation.smartclass_highlight_1 ||
        "Replace scattered WhatsApp groups",
      translation.smartclass_highlight_2 ||
        "Centralized learning resources",
      translation.smartclass_highlight_3 ||
        "Instant school-wide updates",
    ],
    [translation]
  );

  const featureCards = useMemo(
    () => [
      {
        title:
          translation.smartclass_feature_chat_title || "Chat Communication",
        description:
          translation.smartclass_feature_chat_desc ||
          "Real-time messaging between teachers and students with organized channels",
        image: smartclassChat,
        alt:
          translation.smartclass_feature_chat_alt ||
          "SmartClass chat communication preview",
      },
      {
        title: translation.smartclass_feature_knowledge_title || "Knowledge Hub",
        description:
          translation.smartclass_feature_knowledge_desc ||
          "Access notes, materials, and learning resources anytime, anywhere",
        image: smartclassKnowledge,
        alt:
          translation.smartclass_feature_knowledge_alt ||
          "SmartClass knowledge hub preview",
      },
      {
        title:
          translation.smartclass_feature_announcements_title ||
          "Announcements",
        description:
          translation.smartclass_feature_announcements_desc ||
          "Send instant updates across the entire school with push notifications",
        image: smartclassAnnouncements,
        alt:
          translation.smartclass_feature_announcements_alt ||
          "SmartClass announcements preview",
      },
    ],
    [translation]
  );

  const earlyAccessBenefits = useMemo(
    () => [
      translation.smartclass_benefit_1 ||
        "Free access for the first 3 months",
      translation.smartclass_benefit_2 || "Priority onboarding and training",
      translation.smartclass_benefit_3 || "Dedicated support team",
      translation.smartclass_benefit_4 || "Influence future features",
    ],
    [translation]
  );

  const roleOptions = useMemo(
    () => [
      {
        label: translation.smartclass_role_admin || "Admin",
        value: "admin",
      },
      {
        label: translation.smartclass_role_teacher || "Teacher",
        value: "teacher",
      },
      {
        label: translation.smartclass_role_student || "Student",
        value: "student",
      },
      {
        label: translation.smartclass_role_parent || "Parent",
        value: "parent",
      },
    ],
    [translation]
  );

  const studentOptions = useMemo(
    () => [
      {
        label: translation.smartclass_students_1_100 || "1 to 100",
        value: "1-100",
      },
      {
        label: translation.smartclass_students_100_200 || "100 to 200",
        value: "100-200",
      },
      {
        label: translation.smartclass_students_200_300 || "200 to 300",
        value: "200-300",
      },
      {
        label: translation.smartclass_students_300_400 || "300 to 400",
        value: "300-400",
      },
      {
        label: translation.smartclass_students_400_500 || "400 to 500",
        value: "400-500",
      },
      {
        label: translation.smartclass_students_500_600 || "500 to 600",
        value: "500-600",
      },
      {
        label: translation.smartclass_students_600_700 || "600 to 700",
        value: "600-700",
      },
      {
        label: translation.smartclass_students_700_800 || "700 to 800",
        value: "700-800",
      },
      {
        label: translation.smartclass_students_800_900 || "800 to 900",
        value: "800-900",
      },
      {
        label: translation.smartclass_students_900_1000 || "900 to 1000",
        value: "900-1000",
      },
      {
        label: translation.smartclass_students_1000_plus || "1000+",
        value: "1000+",
      },
    ],
    [translation]
  );

  const openModal = () => {
    setIsSubmitted(false);
    setSubmitError("");
    setFormData((current) =>
      getEarlyAccessFormWithUser(publicUser || getStoredPublicUser(), current)
    );
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitted(false);
    setIsSubmitting(false);
    setSubmitError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const getSubmitError = (error) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Unable to submit your request. Please try again."
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    try {
      await createEarlyAccessProfile({
        schoolName: formData.schoolName,
        yourName: formData.contactName,
        role: formData.role,
        email: formData.email,
        phoneNumber: formData.phone,
        numberOfStudents: formData.students,
      });
      setIsSubmitted(true);
      setFormData(initialForm);
    } catch (error) {
      setSubmitError(getSubmitError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToFeatures = () => {
    const target = document.getElementById("smartclass-features");

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="smartclass-page">
      <SmartSchoolSeoContent />
      <LandingHeader />
      <main className="smartclass-main">
        <section className="smartclass-hero-section">
          <div className="landing-container">
            <div className="smartclass-hero-card">
              <div className="smartclass-hero-copy">
                <div className="smartclass-brand">
                  <img
                    src={smartclassLogo}
                    alt={
                      translation.smartclass_logo_alt ||
                      "SmartClass Digital School System"
                    }
                    className="smartclass-brand-logo"
                  />
                </div>

                <h1 className="smartclass-title">
                  <span>{translation.smartclass_hero_title_line_1 || "Smart"}</span>
                  <span className="accent">
                    {translation.smartclass_hero_title_line_2 ||
                      "Communication"}
                  </span>
                  <span>
                    {translation.smartclass_hero_title_line_3 ||
                      "for Modern Schools"}
                  </span>
                </h1>

                <p className="smartclass-subtitle">
                  {translation.smartclass_hero_subtitle ||
                    "A complete digital system for modern schools"}
                </p>

                <p className="smartclass-description">
                  <strong>
                    {translation.smartclass_hero_brand || "SmartClass"}
                  </strong>{" "}
                  {translation.smartclass_hero_description_before_emphasis ||
                    "connects teachers, students, and school management in one platform with"}{" "}
                  <b>
                    {translation.smartclass_hero_description_emphasis ||
                      "real-time communication"}
                  </b>
                  {translation.smartclass_hero_description_after_emphasis ||
                    ", announcements, and learning resources."}
                </p>

                <div className="smartclass-hero-actions">
                  <button
                    type="button"
                    className="smartclass-btn smartclass-btn-primary"
                    onClick={openModal}
                  >
                    {translation.smartclass_hero_primary_cta ||
                      "Get Early Access"}
                  </button>
                  <button
                    type="button"
                    className="smartclass-btn smartclass-btn-secondary"
                    onClick={scrollToFeatures}
                  >
                    {translation.smartclass_hero_secondary_cta ||
                      "Explore Demo"}
                  </button>
                </div>
              </div>

              <div className="smartclass-hero-media">
                <img
                  src={smartclassHero}
                  alt={
                    translation.smartclass_hero_image_alt ||
                    "SmartClass dashboard on laptop and mobile"
                  }
                />
              </div>
            </div>
          </div>
        </section>

        <section className="smartclass-highlights">
          <div className="landing-container">
            <h2>
              {translation.smartclass_highlights_heading ||
                "One Platform. Complete School System."}
            </h2>
            <div className="smartclass-highlight-grid">
              {highlightItems.map((item) => (
                <div key={item} className="smartclass-highlight-item">
                  <span className="smartclass-highlight-dot" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="smartclass-features"
          className="smartclass-features-section"
        >
          <div className="landing-container">
            <div className="smartclass-feature-grid">
              {featureCards.map((card) => (
                <article key={card.title} className="smartclass-feature-card">
                  <div className="smartclass-feature-image-wrap">
                    <img src={card.image} alt={card.alt} />
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="smartclass-trust-section">
          <div className="landing-container">
            <p className="smartclass-eyebrow">
              {translation.smartclass_trust_eyebrow ||
                "Trusted by Modern Schools"}
            </p>
            <h2>
              {translation.smartclass_trust_heading ||
                "10+ schools joining early access"}
            </h2>
          </div>
        </section>

        <section className="smartclass-cta-section">
          <div className="landing-container">
            <div className="smartclass-cta-card">
              <h2>
                {translation.smartclass_cta_heading ||
                  "Get Early Access to SmartClass"}
              </h2>
              <p>
                {translation.smartclass_cta_subheading ||
                  "Be among the first schools to experience a complete digital system"}
              </p>
              <button
                type="button"
                className="smartclass-btn smartclass-btn-white"
                onClick={openModal}
              >
                {translation.smartclass_cta_button || "Get Started"}
              </button>
              <span>
                {translation.smartclass_cta_note ||
                  "No credit card required • Free for early adopters"}
              </span>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />

      {isModalOpen && (
        <div className="smartclass-modal-overlay" onClick={closeModal}>
          <div
            className={`smartclass-modal ${isSubmitted ? "success" : ""}`}
            onClick={(event) => event.stopPropagation()}
          >
            {!isSubmitted ? (
              <>
                <div className="smartclass-modal-banner">
                  <div>
                    <h3>
                      {translation.smartclass_modal_title ||
                        "Get Early Access"}
                    </h3>
                    <p>
                      {translation.smartclass_modal_subtitle ||
                        "Join forward-thinking schools transforming education"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="smartclass-close-btn"
                    aria-label="Close early access modal"
                    onClick={closeModal}
                  >
                    <FiX />
                  </button>
                </div>

                <form
                  className="smartclass-form-shell"
                  onSubmit={handleSubmit}
                >
                  <div className="smartclass-form-grid">
                    <label>
                      <span>
                        {translation.smartclass_form_school_name_label ||
                          "School Name *"}
                      </span>
                      <input
                        name="schoolName"
                        value={formData.schoolName}
                        onChange={handleChange}
                        placeholder={
                          translation.smartclass_form_school_name_placeholder ||
                          "Enter your school name"
                        }
                        required
                      />
                    </label>
                    <label>
                      <span>
                        {translation.smartclass_form_name_label ||
                          "Your Name *"}
                      </span>
                      <input
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        placeholder={
                          translation.smartclass_form_name_placeholder ||
                          "Enter your full name"
                        }
                        required
                      />
                    </label>
                    <label>
                      <span>
                        {translation.smartclass_form_role_label ||
                          "Your Role *"}
                      </span>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">
                          {translation.smartclass_form_role_placeholder ||
                            "Select your role"}
                        </option>
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>
                        {translation.smartclass_form_email_label ||
                          "Email Address *"}
                      </span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={
                          translation.smartclass_form_email_placeholder ||
                          "your.email@school.com"
                        }
                        required
                      />
                    </label>
                    <label>
                      <span>
                        {translation.smartclass_form_phone_label ||
                          "Phone Number *"}
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={
                          translation.smartclass_form_phone_placeholder ||
                          "+91 98765 43210"
                        }
                        required
                      />
                    </label>
                    <label>
                      <span>
                        {translation.smartclass_form_students_label ||
                          "Number of Students *"}
                      </span>
                      <select
                        name="students"
                        value={formData.students}
                        onChange={handleChange}
                        required
                      >
                        <option value="">
                          {translation.smartclass_form_students_placeholder ||
                            "Select student range"}
                        </option>
                        {studentOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="smartclass-benefit-strip">
                    <h4>
                      {translation.smartclass_benefits_heading ||
                        "Early Access Benefits:"}
                    </h4>
                    <div className="smartclass-benefit-list">
                      {earlyAccessBenefits.map((item) => (
                        <span key={item}>
                          <FiCheck />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  {submitError && (
                    <div className="smartclass-form-error">{submitError}</div>
                  )}

                  <button
                    type="submit"
                    className="smartclass-btn smartclass-btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? translation.smartclass_form_submitting ||
                        "Submitting..."
                      : translation.smartclass_form_submit ||
                        "Submit Request"}
                  </button>
                  <p className="smartclass-form-note">
                    {translation.smartclass_form_note ||
                      "We'll contact you within 24 hours to get you started"}
                  </p>
                </form>
              </>
            ) : (
              <div className="smartclass-success-state">
                <div className="smartclass-success-icon">
                  <FiCheck />
                </div>
                <h3>
                  {translation.smartclass_success_title ||
                    "Request Submitted!"}
                </h3>
                <p>
                  {translation.smartclass_success_line_1 ||
                    "Thank you for your interest in SmartClass."}
                  <br />
                  {translation.smartclass_success_line_2 ||
                    "We're excited to have you join us!"}
                </p>
                <strong>
                  {translation.smartclass_success_note ||
                    "Our team will reach out within 24 hours"}
                </strong>
                <button
                  type="button"
                  className="smartclass-btn smartclass-btn-primary smartclass-success-close"
                  onClick={closeModal}
                >
                  <FiSend />
                  {translation.smartclass_success_close || "Close"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartSchoolSystem;
